import { debugLog } from '../debugLog';

export interface PerformanceElementTiming extends PerformanceEntry {
    identifier: string;
    element: HTMLElement | SVGElement | null;
    renderTime: number;
}

function isElementTimingEntry(entry: PerformanceEntry): entry is PerformanceElementTiming {
    return entry.entryType === 'element';
}

type HeroElementLoadedEvent = {
    identifier: string;
    renderTimeFromLastNavigation: number;
};

/**
 * Класс для сбора метрики Element Timing.
 *
 * Метрика ElementTiming измеряет время от загрузки страницы (включая SPA переходы) до отрисовки "hero" элемента,
 * после отрисовки которого мы считаем, что страница загружена и с ней можно работать
 */
export class ElementTimingObserver {
    private observer: PerformanceObserver;
    private elementTimingSent = new Set<string>();
    private timeFromPageChanged = 0;

    constructor(onHeroRender: (e: HeroElementLoadedEvent) => void) {
        // Подписываемся на появление элементов с аттрибутом elementtiming
        this.observer = new PerformanceObserver(list => {
            list.getEntries()
                .filter(isElementTimingEntry)
                .forEach(entry => {
                    // Если на странице уже был hero element с таким именем
                    // (например, при виртуализации, поиске, etc.) - не отсылаем метрику повторно
                    if (this.elementTimingSent.has(entry.identifier)) {
                        return;
                    }
                    this.elementTimingSent.add(entry.identifier);

                    const renderTimeFromLastNavigation = entry.renderTime - this.timeFromPageChanged;

                    debugLog(() => ({
                        tags: ['ElementTiming', entry.identifier, renderTimeFromLastNavigation.toFixed(2)],
                        details: {
                            renderTime: entry.renderTime,
                            timeFromPageChanged: this.timeFromPageChanged,
                        },
                        htmlRef: entry.element,
                    }));

                    onHeroRender({ identifier: entry.identifier, renderTimeFromLastNavigation });
                });
        });
    }

    /**
     * Перезапускает observer с указанным временем начала страницы.
     * Очищает состояние.
     * @param pageStartTime временная метка начала страницы (значение из performance.now())
     * @param trackBuffered получить записи, которые были записаны до вызова этого метода
     */
    restart({ pageStartTime, trackBuffered }: { pageStartTime: number; trackBuffered?: boolean }) {
        this.timeFromPageChanged = pageStartTime;
        this.elementTimingSent = new Set<string>();
        this.observer.observe({ type: 'element', buffered: trackBuffered });
    }

    /**
     * Приостанавливает сбор новых метрик.
     */
    stop() {
        debugLog(() => ({ tags: ['ElementTiming', 'pause'] }));
        this.observer.disconnect();
    }
}
